import { formatInfiniteQueryData } from '@/queries/utils';
import { CustomResponse } from '@/services/api-services/type';
import { DefaultEntity } from '@/types';
import { Loading } from '@amili-v3/amili-v3-design';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { DefaultOptionType } from 'antd/es/select';
import { debounce, get, throttle } from 'lodash';
import { UIEvent } from 'react';

// e.g., BuildTuple<3> will be [any, any, any]
type BuildTuple<L extends number, R extends any[] = []> = R['length'] extends L
  ? R
  : BuildTuple<L, [...R, any]>;

// It creates a tuple of length N, then infers the type of its "tail".
// The length of the tail is N-1.
type Decrement<N extends number> =
  BuildTuple<N> extends [any, ...infer Rest] ? Rest['length'] : never;

/**
 * Creates a union of dot-notation paths for a given object type.
 *
 * @template T The object type to generate paths for.
 * @template D The maximum recursion depth (defaults to 5).
 */
export type Path<T, D extends number = 5> = T extends object
  ? {
      [K in keyof T & string]: D extends 0 // Base case: If D is 0, we stop. `Decrement<0>` resolves to `never`.
        ? `${K}`
        : NonNullable<T[K]> extends readonly any[]
          ? `${K}`
          : NonNullable<T[K]> extends object
            ? // In the recursive call, use Decrement<D> instead of Prev[D]
              `${K}` | `${K}.${Path<NonNullable<T[K]>, Decrement<D>>}`
            : `${K}`;
    }[keyof T & string]
  : never;

// To prevent displaying raw IDs as default option labels, use initValue to insert a temporary empty option so the dropdown looks blank until the actual options are loaded from the API.
// Use this when handle FetchNextPage tills we found the initValue.
export const transformToSelectOptions = <E extends DefaultEntity>(
  data: (DefaultEntity & { name?: string })[],
  fieldNames?: Path<E>[],
  initValues: string[] | null = [],
) => {
  if (initValues && initValues?.length > 0) {
    const remainIds = initValues.filter(
      id => !data?.some(item => item.id === id),
    );

    if (remainIds.length > 0) {
      return [
        ...data.map(item => ({
          label:
            fieldNames
              ?.map(fieldName => {
                return get(item, fieldName as keyof typeof item);
              })
              ?.join(' ') ?? item.name,
          value: item.id,
        })),
        ...remainIds.map(id => ({
          label: '...',
          value: id,
        })),
      ] as DefaultOptionType[];
    }
  }

  return data.map(item => ({
    label:
      fieldNames
        ?.map(fieldName => {
          return get(item, fieldName as keyof typeof item);
        })
        ?.join(' ') ?? item.name,
    value: item.id,
  })) as DefaultOptionType[];
};

export const transformInfiniteQueryToSelectOptions = <E extends DefaultEntity>(
  query: UseInfiniteQueryResult<
    InfiniteData<CustomResponse<E[]>, unknown>,
    Error
  >,
  fieldNames?: Path<E>[],
  initValues: string[] | null = [],
) => {
  return query?.data
    ? transformToSelectOptions(
        formatInfiniteQueryData(query.data),
        fieldNames,
        initValues,
      )
    : [];
};

const handleScrollSelectPopupToGetNextPage = throttle(
  ({
    data,
    event,
    isFetching,
    fetchNextPageFn,
  }: {
    isFetching: boolean;
    fetchNextPageFn: () => void;
    event: UIEvent<HTMLDivElement, globalThis.UIEvent>;
    data: InfiniteData<CustomResponse<unknown>, unknown> | undefined;
  }) => {
    const target = event.target as HTMLDivElement;

    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 100 &&
      !isFetching &&
      data?.pages?.at(-1)?.hasNextPage
    ) {
      fetchNextPageFn();
    }
  },
  200,
);

export const handleScrollPopupToGetNextPage =
  (
    query: UseInfiniteQueryResult<
      InfiniteData<CustomResponse<any[]>, unknown>,
      Error
    >,
  ) =>
  (event: UIEvent<HTMLDivElement, globalThis.UIEvent>) => {
    handleScrollSelectPopupToGetNextPage({
      fetchNextPageFn: query.fetchNextPage,
      isFetching: query.isFetching,
      data: query.data,
      event,
    });
  };

// Callers invoke this inline on every render (e.g. `onSearch: handleSearchSelect(setX)`),
// so debounce instances are cached per setter - otherwise each render creates a fresh
// timer without cancelling the previous one, and orphaned timers all eventually fire.
const debouncedSearchSelectCache = new WeakMap<
  (value: string) => void,
  ReturnType<typeof debounce>
>();

export const handleSearchSelect = (setSearchValue: (value: string) => void) => {
  const cached = debouncedSearchSelectCache.get(setSearchValue);

  if (cached) return cached;

  const debounced = debounce(setSearchValue, 500);
  debouncedSearchSelectCache.set(setSearchValue, debounced);

  return debounced;
};

export const handleFilterOption = (input: string, option: any) => {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
};

export const handleDropdownVisibleChange =
  (setSearchValue: (value: string) => void) => (visible: boolean) => {
    if (!visible) {
      setSearchValue('');
    }
  };

export const addLoadingOption = (
  options: (DefaultOptionType | { label: string; value: string })[],
) => {
  return [
    ...options,
    { label: <Loading size="small" />, value: 'loading-placeholder' },
  ];
};
